import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { publicRequest, privateRequest } from "../utils/api";

// Types based on backend models and API responses
export interface CoreValueTranslation {
  id?: number;
  core_value_id?: number;
  locale: string;
  title: string;
  description?: string;
}

export interface CoreValue {
  id: number;
  title?: string;
  description?: string;
  icon?: string;
  color?: string;
  gradient?: string;
  order: number;
  translations?: CoreValueTranslation[];
  created_at?: string;
  updated_at?: string;
}

export interface CoreValuesResponse {
  core_values: CoreValue[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  message?: string;
  status?: string;
}

export interface CoreValueResponse {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  color?: string;
  gradient?: string;
  order: number;
  translations?: CoreValueTranslation[];
  created_at?: string;
  updated_at?: string;
}

export interface CoreValuesQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  admin?: string;
}

// Query keys
export const CORE_VALUES_QUERY_KEY = ["core-values"];
export const CORE_VALUE_QUERY_KEY = (id: number) => ["core-values", id];

// Fetch core values with pagination
const fetchCoreValues = async (
  params: CoreValuesQueryParams = {}
): Promise<CoreValuesResponse> => {
  try {
    const response = await publicRequest.get("/core-values", { params });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching core values:", error);
    throw error;
  }
};

// Fetch single core value
const fetchCoreValue = async (id: number): Promise<CoreValueResponse> => {
  try {
    const response = await publicRequest.get(`/core-values/${id}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching core value:", error);
    throw error;
  }
};

// Fetch core value for admin (with translations)
const fetchCoreValueAdmin = async (id: number): Promise<CoreValueResponse> => {
  try {
    const response = await privateRequest.get(`/core-values/${id}?admin=true`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching core value for admin:", error);
    throw error;
  }
};

// Create core value (admin only)
const createCoreValue = async (coreValueData: {
  icon?: string;
  color?: string;
  gradient?: string;
  order?: number;
  translations: CoreValueTranslation[];
}): Promise<{ core_value: CoreValueResponse }> => {
  try {
    const response = await privateRequest.post("/core-values", coreValueData);
    return response.data.data;
  } catch (error) {
    console.error("Error creating core value:", error);
    throw error;
  }
};

// Update core value (admin only)
const updateCoreValue = async ({
  id,
  coreValueData,
}: {
  id: number;
  coreValueData: {
    icon?: string;
    color?: string;
    gradient?: string;
    order?: number;
    translations?: CoreValueTranslation[];
  };
}): Promise<{ core_value: CoreValueResponse }> => {
  try {
    const response = await privateRequest.put(
      `/core-values/${id}`,
      coreValueData
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating core value:", error);
    throw error;
  }
};

// Delete core value (admin only)
const deleteCoreValue = async (id: number): Promise<void> => {
  try {
    await privateRequest.delete(`/core-values/${id}`);
  } catch (error) {
    console.error("Error deleting core value:", error);
    throw error;
  }
};

// Reorder core values (admin only)
const reorderCoreValues = async (coreValuesData: {
  core_values: { id: number; order: number }[];
}): Promise<void> => {
  try {
    await privateRequest.post("/core-values/reorder", coreValuesData);
  } catch (error) {
    console.error("Error reordering core values:", error);
    throw error;
  }
};

// Custom hooks
export const useCoreValues = (params: CoreValuesQueryParams = {}) => {
  const result = useQuery({
    queryKey: [...CORE_VALUES_QUERY_KEY, params],
    queryFn: () => fetchCoreValues(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return result;
};

// Admin hook that includes translations
export const useCoreValuesAdmin = (params: CoreValuesQueryParams = {}) => {
  const result = useQuery({
    queryKey: [...CORE_VALUES_QUERY_KEY, "admin", params],
    queryFn: () => fetchCoreValues({ ...params, admin: "true" }),
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });

  return result;
};

export const useCoreValue = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: CORE_VALUE_QUERY_KEY(id),
    queryFn: () => fetchCoreValue(id),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCoreValueAdmin = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...CORE_VALUE_QUERY_KEY(id), "admin"],
    queryFn: () => fetchCoreValueAdmin(id),
    enabled,
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateCoreValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCoreValue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CORE_VALUES_QUERY_KEY });
    },
  });
};

export const useUpdateCoreValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCoreValue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CORE_VALUES_QUERY_KEY });
    },
  });
};

export const useDeleteCoreValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCoreValue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CORE_VALUES_QUERY_KEY });
    },
  });
};

export const useReorderCoreValues = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderCoreValues,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CORE_VALUES_QUERY_KEY });
    },
  });
};
